import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { useState } from "react";
import { MessageSquare, Bot, Coins, DollarSign } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";
import { BudgetCard } from "@/components/usage/BudgetCard";

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(142 76% 36%)",
  "hsl(280 67% 60%)",
  "hsl(20 90% 55%)",
];

export default function Usage() {
  const [selectedOrg, setSelectedOrg] = useState<string>("all");
  const [period, setPeriod] = useState<string>("30");

  const { data: orgs = [] } = useQuery({
    queryKey: ["usage-orgs"],
    queryFn: async () => {
      const { data } = await supabase.from("organizations").select("id, name").order("name");
      return data ?? [];
    },
  });

  // Usage tracking data
  const { data: usage = [], isLoading } = useQuery({
    queryKey: ["usage-tracking", selectedOrg],
    queryFn: async () => {
      let q = supabase.from("usage_tracking").select("*").order("period_key", { ascending: false }).limit(50);
      if (selectedOrg !== "all") q = q.eq("org_id", selectedOrg);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  // Model decisions for cost analytics
  const { data: decisions = [] } = useQuery({
    queryKey: ["model-decisions-usage", selectedOrg, period],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - Number(period));
      let q = supabase
        .from("model_decisions")
        .select("model_used, provider_used, estimated_cost_usd, latency_ms, success, created_at")
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: true })
        .limit(1000);
      if (selectedOrg !== "all") q = q.eq("org_id", selectedOrg);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  // Aggregate usage metrics
  const metricTotals: Record<string, number> = {};
  usage.forEach((u) => {
    metricTotals[u.metric] = (metricTotals[u.metric] || 0) + (u.value || 0);
  });

  // Cost aggregations
  const totalCost = decisions.reduce((s, d) => s + (d.estimated_cost_usd || 0), 0);
  const avgCost = decisions.length ? totalCost / decisions.length : 0;
  const totalTokens = metricTotals["tokens"] || 0;

  // Cost by model (pie)
  const costByModel: Record<string, number> = {};
  decisions.forEach((d) => {
    costByModel[d.model_used] = (costByModel[d.model_used] || 0) + (d.estimated_cost_usd || 0);
  });
  const pieData = Object.entries(costByModel)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([name, value]) => ({ name, value: +value.toFixed(4) }));

  // Cost by provider (bar)
  const costByProvider: Record<string, { cost: number; calls: number; tokens: number }> = {};
  decisions.forEach((d) => {
    const p = d.provider_used || "unknown";
    if (!costByProvider[p]) costByProvider[p] = { cost: 0, calls: 0, tokens: 0 };
    costByProvider[p].cost += d.estimated_cost_usd || 0;
    costByProvider[p].calls += 1;
  });
  const providerData = Object.entries(costByProvider)
    .sort(([, a], [, b]) => b.cost - a.cost)
    .map(([provider, s]) => ({
      provider,
      cost: +s.cost.toFixed(4),
      calls: s.calls,
    }));

  // Cost by day (line)
  const costByDay: Record<string, number> = {};
  decisions.forEach((d) => {
    const day = (d.created_at ?? "").slice(0, 10);
    if (day) costByDay[day] = (costByDay[day] || 0) + (d.estimated_cost_usd || 0);
  });
  const lineData = Object.entries(costByDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, cost]) => ({ date, cost: +cost.toFixed(4) }));

  // Top models table
  const modelStats: Record<string, { calls: number; cost: number; latency: number[]; success: number }> = {};
  decisions.forEach((d) => {
    if (!modelStats[d.model_used]) modelStats[d.model_used] = { calls: 0, cost: 0, latency: [], success: 0 };
    const m = modelStats[d.model_used];
    m.calls++;
    m.cost += d.estimated_cost_usd || 0;
    if (d.latency_ms) m.latency.push(d.latency_ms);
    if (d.success) m.success++;
  });
  const topModels = Object.entries(modelStats)
    .sort(([, a], [, b]) => b.calls - a.calls)
    .slice(0, 10)
    .map(([model, s]) => ({
      model,
      calls: s.calls,
      cost: +s.cost.toFixed(4),
      avgLatency: s.latency.length ? Math.round(s.latency.reduce((a, b) => a + b, 0) / s.latency.length) : 0,
      successRate: s.calls ? +((s.success / s.calls) * 100).toFixed(1) : 0,
    }));

  // Usage chart
  const periodMap: Record<string, Record<string, number>> = {};
  usage.forEach((u) => {
    if (!periodMap[u.period_key]) periodMap[u.period_key] = {};
    periodMap[u.period_key][u.metric] = (periodMap[u.period_key][u.metric] || 0) + (u.value || 0);
  });
  const chartData = Object.entries(periodMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([key, metrics]) => ({ period: key, ...metrics }));

  const kpis = [
    { title: "Custo Total IA", value: `$${totalCost.toFixed(4)}`, icon: DollarSign, color: "text-primary" },
    { title: "Custo Médio/Chamada", value: `$${avgCost.toFixed(6)}`, icon: Coins, color: "text-warning" },
    { title: "Mensagens", value: (metricTotals["messages"] || 0).toLocaleString(), icon: MessageSquare, color: "text-info" },
    { title: "Chamadas IA", value: decisions.length.toLocaleString(), icon: Bot, color: "text-primary" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-foreground font-headline">Uso & Consumo</h1>
        <div className="flex items-center gap-2">
          <ExportButton
            data={topModels}
            filename="uso_modelos"
            columns={[
              { key: "model", label: "Modelo" },
              { key: "calls", label: "Chamadas" },
              { key: "cost", label: "Custo USD" },
              { key: "avgLatency", label: "Latência Média (ms)" },
              { key: "successRate", label: "Taxa Sucesso (%)" },
            ]}
          />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todas as orgs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as organizações</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((c) => (
              <Card key={c.title}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{c.title}</CardTitle>
                  <c.icon className={`w-5 h-5 ${c.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground font-headline">{c.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Budget */}
          <BudgetCard orgId={selectedOrg} spentUsd={totalCost} />

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost by Model */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Custo por Modelo</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-8">Sem dados.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name.split("/").pop()} ${(percent * 100).toFixed(0)}%`}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v: number) => `$${v.toFixed(4)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Cost by Day */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Custo Diário (USD)</CardTitle>
              </CardHeader>
              <CardContent>
                {lineData.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-8">Sem dados.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={lineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                      <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                      <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }} />
                      <Line type="monotone" dataKey="cost" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cost by Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Custo & Chamadas por Provider</CardTitle>
            </CardHeader>
            <CardContent>
              {providerData.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">Sem dados.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={providerData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis dataKey="provider" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }} formatter={(v: number, name: string) => name === "Custo (USD)" ? `$${v.toFixed(4)}` : v} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="cost" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Custo (USD)" />
                    <Bar yAxisId="right" dataKey="calls" fill="hsl(var(--info))" radius={[4, 4, 0, 0]} name="Chamadas" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Usage by Period */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Uso por Período</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">Sem dados de uso ainda.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis dataKey="period" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "none", borderRadius: 12, color: "hsl(var(--foreground))" }} />
                    <Bar dataKey="messages" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Mensagens" />
                    <Bar dataKey="ai_calls" fill="hsl(var(--info))" radius={[4, 4, 0, 0]} name="Chamadas IA" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Top Models Table */}
          {topModels.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">Top Modelos</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Modelo</TableHead>
                      <TableHead className="text-right">Chamadas</TableHead>
                      <TableHead className="text-right">Custo (USD)</TableHead>
                      <TableHead className="text-right">Latência Média</TableHead>
                      <TableHead className="text-right">Sucesso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topModels.map((m) => (
                      <TableRow key={m.model}>
                        <TableCell className="font-mono text-sm">{m.model}</TableCell>
                        <TableCell className="text-right">{m.calls}</TableCell>
                        <TableCell className="text-right">${m.cost.toFixed(4)}</TableCell>
                        <TableCell className="text-right">{m.avgLatency}ms</TableCell>
                        <TableCell className="text-right">{m.successRate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
